import React from 'react';
import { motion } from 'framer-motion';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

export function cn(...inputs) {
  return twMerge(clsx(inputs));
}

// Button Component
export const Button = React.forwardRef(({ 
  className, 
  variant = 'default', 
  size = 'default',
  children,
  ...props 
}, ref) => {
  const variants = {
    default: 'bg-primary text-primary-foreground hover:bg-primary/90',
    outline: 'border border-primary text-primary hover:bg-primary hover:text-primary-foreground',
    ghost: 'text-white hover:bg-accent hover:text-accent-foreground',
    destructive: 'bg-destructive text-destructive-foreground hover:bg-destructive/90',
    secondary: 'bg-secondary text-secondary-foreground hover:bg-secondary/80',
  };

  const sizes = {
    default: 'px-6 py-3',
    sm: 'px-4 py-2 text-sm',
    lg: 'px-8 py-4 text-lg',
    icon: 'p-3',
  };

  return (
    <motion.button
      ref={ref}
      whileHover={{ scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      className={cn(
        'inline-flex items-center justify-center gap-2 font-heading tracking-wider text-sm transition-all duration-200 disabled:opacity-50 disabled:pointer-events-none rounded-sm',
        variants[variant],
        sizes[size],
        className
      )}
      {...props}
    >
      {children}
    </motion.button>
  );
});

// Card Component
export const Card = React.forwardRef(({ className, children, ...props }, ref) => (
  <motion.div
    ref={ref}
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    className={cn(
      'bg-background-secondary border border-border rounded-sm transition-all duration-300 hover:border-primary/50',
      className
    )}
    {...props}
  >
    {children}
  </motion.div>
));

// Input Component
export const Input = React.forwardRef(({ className, type, ...props }, ref) => (
  <input
    type={type}
    className={cn(
      'w-full px-4 py-3 bg-[#1a1a1a] border border-border text-white placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary transition-all duration-200 rounded-sm outline-none',
      className
    )}
    ref={ref}
    {...props}
  />
));

// Textarea Component
export const Textarea = React.forwardRef(({ className, ...props }, ref) => (
  <textarea
    className={cn(
      'w-full px-4 py-3 bg-[#1a1a1a] border border-border text-white placeholder:text-muted-foreground focus:border-primary focus:ring-1 focus:ring-primary transition-all duration-200 rounded-sm outline-none resize-none',
      className
    )}
    ref={ref}
    {...props}
  />
));

// Select Component
export const Select = React.forwardRef(({ className, children, ...props }, ref) => (
  <select
    className={cn(
      'w-full px-4 py-3 bg-[#1a1a1a] border border-border text-white focus:border-primary focus:ring-1 focus:ring-primary transition-all duration-200 rounded-sm outline-none cursor-pointer',
      className
    )}
    ref={ref}
    {...props}
  >
    {children}
  </select>
));

// Badge Component
export const Badge = ({ className, variant = 'default', children, ...props }) => {
  const variants = {
    default: 'bg-primary/20 text-primary border-primary/30',
    secondary: 'bg-secondary/20 text-secondary border-secondary/30',
    success: 'bg-green-500/20 text-green-400 border-green-500/30',
    warning: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    destructive: 'bg-destructive/20 text-destructive border-destructive/30',
  };

  return (
    <span
      className={cn(
        'inline-flex items-center px-3 py-1 text-xs font-medium border rounded-sm tracking-wider',
        variants[variant],
        className
      )}
      {...props}
    >
      {children}
    </span>
  );
};

// Avatar Component
export const Avatar = ({ src, alt, fallback, className, size = 'default' }) => {
  const sizes = {
    sm: 'w-8 h-8 text-xs',
    default: 'w-10 h-10 text-sm',
    lg: 'w-14 h-14 text-lg',
    xl: 'w-20 h-20 text-xl',
  };

  return (
    <div className={cn(
      'rounded-full overflow-hidden bg-muted flex items-center justify-center',
      sizes[size],
      className
    )}>
      {src ? (
        <img src={src} alt={alt} className="w-full h-full object-cover" />
      ) : (
        <span className="font-heading text-muted-foreground">{fallback}</span>
      )}
    </div>
  );
};

// Modal Component
export const Modal = ({ isOpen, onClose, title, children }) => {
  React.useEffect(() => {
    if (!isOpen) return;
    document.body.style.overflow = 'hidden';
    const handleEsc = (e) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', handleEsc);
    return () => {
      document.body.style.overflow = '';
      document.removeEventListener('keydown', handleEsc);
    };
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title || 'Dialog'}
      onClick={onClose}
    >
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm" />
      <motion.div
        initial={{ scale: 0.95, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.95, opacity: 0 }}
        className="relative bg-background-secondary border border-border rounded-sm w-full max-w-lg max-h-[90vh] overflow-auto"
        onClick={(e) => e.stopPropagation()}
      >
        {title && (
          <div className="px-6 py-4 border-b border-border flex items-center justify-between">
            <h3 className="font-heading text-xl">{title}</h3>
            <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors" aria-label="Kapat">
              &times;
            </button>
          </div>
        )}
        <div className="p-6">{children}</div>
      </motion.div>
    </motion.div>
  );
};

// Loading Spinner
export const Spinner = ({ className, size = 'default' }) => {
  const sizes = {
    sm: 'w-4 h-4',
    default: 'w-6 h-6',
    lg: 'w-10 h-10',
  };

  return (
    <svg
      className={cn('animate-spin text-primary', sizes[size], className)}
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
};

// Progress Bar
export const Progress = ({ value, max = 100, className }) => (
  <div className={cn('w-full h-2 bg-muted rounded-sm overflow-hidden', className)}>
    <motion.div
      initial={{ width: 0 }}
      animate={{ width: `${(value / max) * 100}%` }}
      className="h-full bg-primary"
      transition={{ duration: 0.5, ease: 'easeOut' }}
    />
  </div>
);

// Tabs Component
export const Tabs = ({ tabs, activeTab, onTabChange, className }) => (
  <div className={cn('flex border-b border-border', className)}>
    {tabs.map((tab) => (
      <button
        key={tab.id}
        onClick={() => onTabChange(tab.id)}
        className={cn(
          'px-6 py-3 font-heading text-sm uppercase tracking-wider transition-all duration-200 border-b-2 -mb-px',
          activeTab === tab.id
            ? 'text-primary border-primary'
            : 'text-muted-foreground border-transparent hover:text-white'
        )}
      >
        {tab.label}
      </button>
    ))}
  </div>
);
